// The actions of a quote (Diagnostic Intake), shared by every place that opens
// the quote editor (today the company's Intake tab; next, Personas and the
// common "Cotizaciones y pagos" view). They write through the usual commercial
// path (airtable-proxy) and publish-proposal, where the server checks who may
// operate that quote and what may change (src/quoteRules.js).
import { TABLE_IDS, getAuthHeaders } from './globals.js';
import { FACT_FIELDS, PIPELINE_FACT_WRITES } from './pipelineFacts.js';
import { QUOTE_RULE_MESSAGES } from './quoteRules.js';

/** The message for an error of these actions: the rule's own text when the server refused by a rule. */
export function quoteErrorMessage(e, fallback = 'No se pudo completar') {
  const text = String(e?.message || '');
  const code = Object.keys(QUOTE_RULE_MESSAGES).find(k => text.includes(k));
  return code ? QUOTE_RULE_MESSAGES[code] : `${fallback}: ${text}`;
}

export async function saveQuoteRecord(api, id, fields) {
  await api.updateRecord(TABLE_IDS.diagnosticIntake, id, fields);
  return fields;
}

/** Approving is internal: it says nothing about a meeting (src/pipelineFacts.js). */
export async function approveQuoteRecord(api, id) {
  const fields = { 'Quote Status': 'Approved', ...(PIPELINE_FACT_WRITES ? { [FACT_FIELDS.quoteApprovedAt]: new Date().toISOString() } : {}) };
  await api.updateRecord(TABLE_IDS.diagnosticIntake, id, fields);
  return fields;
}

/**
 * Publishing makes the quote visible to the client (her space; the link of the document). Sending again
 * updates the same document and link and adds its date and total to the history (publish-proposal).
 * An internal approval is optional. Returns { landingId, proposalSlug, snapshot, updated }.
 */
const PUBLISH_MESSAGES = {
  quote_lost: 'La cotización está marcada como perdida: no se publica.',
  quote_no_lines: 'Agregá al menos una línea antes de publicar.',
  link_failed: 'El documento se generó, pero no se pudo vincular a la cotización. Nada se perdió: reintentá y se usa ese mismo documento.',
  landing_mismatch: 'La cotización cambió desde el intento anterior: publicala de nuevo.',
};
/**
 * The error keeps what the server said: its HTTP status, its code and, after `link_failed`, the id of the document
 * already created, so a retry (`landingId`) reuses it on the same record instead of creating another.
 */
export async function publishQuoteRecord(id, { landingId } = {}) {
  let res;
  try {
    res = await fetch('/api/publish-proposal', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ intakeId: id, ...(landingId ? { landingId } : {}) }) });
  } catch {
    throw Object.assign(new Error('Sin conexión con Oike: no se sabe si se publicó. Reintentá: se usa el mismo registro.'), { code: 'network' });
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const text = PUBLISH_MESSAGES[data.error] || QUOTE_RULE_MESSAGES[data.error] || data.error || 'Publish failed';
    const detail = [`HTTP ${res.status}`, data.error && data.error !== text ? data.error : ''].filter(Boolean).join(' · ');
    throw Object.assign(new Error(`${text} (${detail})`), { status: res.status, code: data.error || '', landingId: data.landingId || '' });
  }
  return data;
}

const CORRECTION_MESSAGES = {
  reason_required: 'Escribí el motivo de la corrección (10 a 500 caracteres).',
  confirm_required: 'Confirmá la corrección antes de guardarla.',
  day_required: 'Un pago completado necesita el día en que se completó.',
  day_only_received: 'El día va solo con un pago completado.',
  future_day: 'El día no puede ser futuro.',
  invalid_day: 'El día no es válido.',
  invalid_status: 'El estado de pago no es válido.',
  no_change: 'No hay cambios respecto de lo registrado.',
  forbidden: 'Solo una administradora de la base puede hacer esta corrección.',
  quote_has_charges: 'Esta versión tiene cobros: su pago se corrige en Cotizaciones y pagos, no aquí.',
  invalid_type: 'Elegí si fue un error de registro o una cancelación del cliente.',
  not_accepted: 'La cotización ya no figura como aceptada (quizás ya se corrigió).',
  day_only_cancelled: 'El día va solo con una cancelación del cliente.',
};

/**
 * Corrects a payment registered by mistake (quote-correction.mts): only an administrator,
 * with a reason and an explicit confirmation; the server adds the trace to Pipeline Facts Note.
 * status: '' (no payment) | Pending | Partial | Received; receivedOn: 'YYYY-MM-DD' only with Received.
 */
export async function correctPaymentRecord(id, { status, receivedOn, reason }) {
  const res = await fetch('/api/quote-correction', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ intakeId: id, kind: 'payment', status, receivedOn: status === 'Received' ? receivedOn : '', reason, confirm: true }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(CORRECTION_MESSAGES[data.error] || `No se pudo corregir el pago (${data.error || res.status}).`);
  return data;
}

/**
 * Corrects an acceptance (quote-correction.mts): type 'error' (registered by mistake → Approved,
 * no longer won) or 'cancelled' (the client cancelled → Lost, its acceptance date stays). The
 * lines and the document stay locked; payments and services are not touched.
 */
export async function correctAcceptanceRecord(id, { type, cancelledOn, reason }) {
  const res = await fetch('/api/quote-correction', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ intakeId: id, kind: 'acceptance', type, cancelledOn: type === 'cancelled' ? cancelledOn || '' : '', reason, confirm: true }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(CORRECTION_MESSAGES[data.error] || `No se pudo corregir la aceptación (${data.error || res.status}).`);
  return data;
}

const ACCEPT_MESSAGES = {
  confirm_required: 'Confirmá la aceptación antes de registrarla.',
  by_required: 'Escribí quién aceptó (nombre o rol).',
  invalid_day: 'El día no es válido.',
  future_day: 'El día no puede ser futuro: registrá una aceptación que ya ocurrió.',
  unpublished_changes: 'Hay cambios que el cliente todavía no recibió. Publicalos (o deshacelos) antes de registrar la aceptación: se acepta lo que el cliente tiene.',
  quote_lost: 'La cotización está marcada como perdida.',
  quote_no_lines: 'La cotización no tiene líneas.',
  quote_replaced: 'Es una versión anterior: queda como historial.',
  quote_was_accepted: 'Esta cotización ya fue aceptada antes (con una corrección registrada): no se acepta de nuevo aquí.',
  landing_unreadable: 'No se pudo leer el documento publicado: probá de nuevo.',
  write_failed: 'No se pudo guardar la aceptación: probá de nuevo.',
};
/**
 * «Registrar aceptación» (quote-accept.mts): the client accepted outside Oike. Day (YYYY-MM-DD), optional
 * time, who accepted, explicit confirmation. Returns { fields, existing, viaPending, documentNote }.
 */
export async function acceptQuoteRecord(id, { day, time = '', by }) {
  const res = await fetch('/api/quote-accept', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ intakeId: id, day, time, by, confirm: true }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(ACCEPT_MESSAGES[data.error] || QUOTE_RULE_MESSAGES[data.error] || `No se pudo registrar la aceptación (${data.error || res.status}).`);
  return data;
}
